import { Image } from '@chakra-ui/react';
import { User } from 'lucide-react';
import { Text } from './text';
import { HStack, VStack } from './stack';
import { useMemo } from 'react';

type Props = {
  avatarUrl: string;
  hostDisplayName: string;
  meetingTime: string;
  timezone: string | undefined;
};

/**
 * The appointment row on the booking confirmation. It carries no card chrome of
 * its own: the confirmation card wraps this row and the note field together.
 */
export const VisitCard = ({
  avatarUrl,
  hostDisplayName,
  meetingTime,
  timezone,
}: Props) => {
  const date = new Date(meetingTime);

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
        timeZoneName: 'short',
      }),
    [timezone]
  );

  return (
    <HStack
      alignItems={'center'}
      gap={'14px'}
      py={'18px'}
      w={'100%'}
      borderBottomWidth={1}
    >
      <VStack
        alignItems={'center'}
        bg={'#E4EEF1'}
        borderColor={'white'}
        borderWidth={'2px'}
        boxShadow={'0 2px 6px rgba(3, 35, 38, 0.18)'}
        color={'secondary.200'}
        flexShrink={0}
        h={'52px'}
        justifyContent={'center'}
        overflow={'hidden'}
        rounded={'full'}
        w={'52px'}
      >
        {avatarUrl ? (
          <Image
            alt="care team member profile image"
            h={'100%'}
            objectFit="cover"
            src={avatarUrl}
            w={'100%'}
          />
        ) : (
          <User size={28} strokeWidth={1.75} />
        )}
      </VStack>

      <VStack alignItems={'flex-start'} gap={'2px'} flex={1} minW={0}>
        <Text
          color={'secondary.500'}
          fontSize={'15.5px'}
          fontWeight={700}
          textAlign={'left'}
        >
          Appointment with {hostDisplayName}
        </Text>

        {meetingTime && (
          <Text color={'blueGray.600'} fontSize={'13.5px'} textAlign={'left'}>
            {formatter.format(date)}
          </Text>
        )}
      </VStack>
    </HStack>
  );
};
